import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  AiGenerateDropdownButton,
  AiGenerateAction,
  AiEnhancedInput,
  AiEnhancedTextarea,
  AutoResizeTextarea,
  Button,
  Card,
  DescriptiveSelect,
  DropdownMenu,
  DropdownMenuItem,
  FormLabel,
  Input,
  MarkdownEditor,
  Modal,
  SaveButton,
  SectionHeader,
  Select,
  Spinner,
  StatCard,
} from '../components/common';

export default function ComponentsLibraryPage() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [descriptiveSelectValue, setDescriptiveSelectValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [markdownValue, setMarkdownValue] = useState(
    '# Hello World\n\nThis is **bold** and *italic* text.'
  );
  const [showModal, setShowModal] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [aiDropdownContent, setAiDropdownContent] = useState('');
  const [isDropdownGenerating, setIsDropdownGenerating] = useState(false);
  const [lastDropdownAction, setLastDropdownAction] = useState<string>('');
  const [aiInputValue, setAiInputValue] = useState('');
  const [aiTextareaValue, setAiTextareaValue] = useState('');
  const [isAiInputLoading, setIsAiInputLoading] = useState(false);
  const [isAiTextareaLoading, setIsAiTextareaLoading] = useState(false);
  const [lastAiInputAction, setLastAiInputAction] = useState<string>('');
  const [lastAiTextareaAction, setLastAiTextareaAction] = useState<string>('');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Redirect non-admin users
  if (!user?.admin) {
    return <Navigate to="/" replace />;
  }

  const mockSave = async (): Promise<boolean> => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSaveButtonDisabled(true);
    return true;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('admin.title')}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('admin.componentsLibrary')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('admin.componentsLibraryDescription')}
        </p>
      </div>

      <div className="space-y-12">
        {/* Buttons Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Buttons
          </h2>
          <div className="space-y-6">
            {/* Variants */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                Variants
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="accent">Accent</Button>
                <Button variant="danger">Danger</Button>
              </div>
            </div>

            {/* Sizes */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Sizes</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
              </div>
            </div>

            {/* States */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">States</h3>
              <div className="flex flex-wrap gap-3">
                <Button disabled>Disabled</Button>
                <Button isLoading>Loading</Button>
              </div>
            </div>

            {/* With Icons */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                With Icons
              </h3>
              <div className="flex flex-wrap gap-3">
                <Button
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  }
                >
                  Left Icon
                </Button>
                <Button
                  rightIcon={
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  }
                >
                  Right Icon
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SaveButton Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            SaveButton
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            A specialized button that shows loading state during save and displays
            &quot;Saved!&quot; confirmation with checkmark animation on success.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                placeholder="Type something to enable save..."
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setSaveButtonDisabled(false);
                }}
                className="flex-1"
              />
              <SaveButton onSave={mockSave} disabled={saveButtonDisabled} />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;SaveButton onSave=&#123;async () =&gt; boolean&#125;
                disabled=&#123;boolean&#125; /&gt;
              </code>
            </p>
          </div>
        </section>

        {/* AiGenerateDropdownButton Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            AiGenerateDropdownButton
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            A split button with dropdown for AI generation actions. Provides options for generate,
            generate with instructions, rephrase, and open in chat. This is the preferred component
            for AI generation in forms.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                placeholder="Type something to see 'Rephrase' mode..."
                value={aiDropdownContent}
                onChange={(e) => setAiDropdownContent(e.target.value)}
                className="flex-1"
              />
              <AiGenerateDropdownButton
                onAction={(action: AiGenerateAction, instructions?: string) => {
                  setLastDropdownAction(`${action}${instructions ? `: "${instructions}"` : ''}`);
                  setIsDropdownGenerating(true);
                  setTimeout(() => {
                    setAiDropdownContent(`Content generated via: ${action}`);
                    setIsDropdownGenerating(false);
                  }, 1500);
                }}
                isLoading={isDropdownGenerating}
                hasContent={!!aiDropdownContent.trim()}
              />
            </div>
            {lastDropdownAction && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Last action: <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{lastDropdownAction}</code>
              </p>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Variants:</p>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">Default (md)</span>
                  <AiGenerateDropdownButton onAction={() => {}} hasContent={false} />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">Small (sm)</span>
                  <AiGenerateDropdownButton onAction={() => {}} hasContent={false} size="sm" />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">With content</span>
                  <AiGenerateDropdownButton onAction={() => {}} hasContent={true} />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">Loading</span>
                  <AiGenerateDropdownButton onAction={() => {}} isLoading={true} />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">Disabled</span>
                  <AiGenerateDropdownButton onAction={() => {}} disabled={true} />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">No rephrase</span>
                  <AiGenerateDropdownButton onAction={() => {}} hasContent={true} showRephraseOptions={false} />
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-xs text-gray-400">No chat</span>
                  <AiGenerateDropdownButton onAction={() => {}} showChatOption={false} />
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;AiGenerateDropdownButton onAction=&#123;(action, instructions?) =&gt; void&#125;
                isLoading hasContent showRephraseOptions showChatOption size /&gt;
              </code>
            </p>
          </div>
        </section>

        {/* AI Enhanced Inputs Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            AI Enhanced Inputs
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Standard-looking input and textarea with two AI icons in the top-right corner.
            The first icon runs the <strong>quick action</strong> (dice when empty for &quot;random&quot;,
            sparkles when content exists for &quot;enhance&quot;). The second icon (three dots) opens a
            dropdown with the full set of options (generate, generate with instructions, rephrase, etc.).
            Prefer these over the legacy button-under-input pattern for new forms.
          </p>

          <div className="space-y-8">
            {/* AiEnhancedInput */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                AiEnhancedInput
              </h3>
              <AiEnhancedInput
                label="Book title"
                placeholder="Type a title or let AI pick one..."
                helperText="Click the dice for a random title, or the three dots for more options."
                value={aiInputValue}
                onChange={(e) => setAiInputValue(e.target.value)}
                isLoading={isAiInputLoading}
                onQuickAction={() => {
                  setLastAiInputAction('quickAction');
                  setIsAiInputLoading(true);
                  setTimeout(() => {
                    setAiInputValue('The Whispering Garden');
                    setIsAiInputLoading(false);
                  }, 900);
                }}
                onMenuAction={(action, instructions) => {
                  setLastAiInputAction(
                    `${action}${instructions ? `: "${instructions}"` : ''}`
                  );
                  setIsAiInputLoading(true);
                  setTimeout(() => {
                    setAiInputValue(`Title via ${action}`);
                    setIsAiInputLoading(false);
                  }, 900);
                }}
              />
              {lastAiInputAction && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Last action:{' '}
                  <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                    {lastAiInputAction}
                  </code>
                </p>
              )}
            </div>

            {/* AiEnhancedTextarea */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                AiEnhancedTextarea
              </h3>
              <AiEnhancedTextarea
                label="Summary"
                placeholder="Write a summary, or let AI draft one..."
                helperText="Icons anchor to the top-right of the textarea."
                minRows={4}
                maxRows={12}
                value={aiTextareaValue}
                onChange={(e) => setAiTextareaValue(e.target.value)}
                isLoading={isAiTextareaLoading}
                onQuickAction={() => {
                  setLastAiTextareaAction('quickAction');
                  setIsAiTextareaLoading(true);
                  setTimeout(() => {
                    setAiTextareaValue(
                      'A lonely lighthouse keeper discovers a message in a bottle that rewrites her past.'
                    );
                    setIsAiTextareaLoading(false);
                  }, 900);
                }}
                onMenuAction={(action, instructions) => {
                  setLastAiTextareaAction(
                    `${action}${instructions ? `: "${instructions}"` : ''}`
                  );
                  setIsAiTextareaLoading(true);
                  setTimeout(() => {
                    setAiTextareaValue(`Summary via ${action}`);
                    setIsAiTextareaLoading(false);
                  }, 900);
                }}
              />
              {lastAiTextareaAction && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Last action:{' '}
                  <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                    {lastAiTextareaAction}
                  </code>
                </p>
              )}
            </div>

            {/* Quick action icon variants */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                Quick action icon variants
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <AiEnhancedInput
                  placeholder="dice (random)"
                  quickActionIcon="dice"
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
                <AiEnhancedInput
                  placeholder="sparkles (enhance)"
                  quickActionIcon="sparkles"
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
                <AiEnhancedInput
                  placeholder="wand"
                  quickActionIcon="wand"
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
                <AiEnhancedInput
                  placeholder="auto: dice when empty, sparkles when filled"
                  quickActionIcon="auto"
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
              </div>
            </div>

            {/* States */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">States</h3>
              <div className="space-y-3">
                <AiEnhancedInput
                  placeholder="Loading state"
                  isLoading
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
                <AiEnhancedInput
                  placeholder="Disabled"
                  disabled
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
                <AiEnhancedInput
                  placeholder="With error"
                  error="This field is required"
                  onQuickAction={() => {}}
                  onMenuAction={() => {}}
                />
              </div>
            </div>

            <div className="text-xs text-gray-400 dark:text-gray-500 space-y-1">
              <p>
                Usage:{' '}
                <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                  &lt;AiEnhancedInput value onChange onQuickAction onMenuAction
                  isLoading quickActionIcon /&gt;
                </code>
              </p>
              <p>
                Usage:{' '}
                <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                  &lt;AiEnhancedTextarea value onChange onQuickAction onMenuAction
                  minRows maxRows /&gt;
                </code>
              </p>
            </div>
          </div>
        </section>

        {/* Form Inputs Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Form Inputs
          </h2>
          <div className="space-y-6">
            {/* Input */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Input</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input placeholder="Default input" />
                <Input placeholder="Disabled input" disabled />
              </div>
            </div>

            {/* Select */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Select</h3>
              <Select
                value={selectValue}
                onChange={(e) => setSelectValue(e.target.value)}
                className="max-w-xs"
                placeholder="Choose an option..."
                options={[
                  { value: '1', label: 'Option 1' },
                  { value: '2', label: 'Option 2' },
                  { value: '3', label: 'Option 3' },
                ]}
              />
            </div>

            {/* DescriptiveSelect */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                DescriptiveSelect
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                A rich select component with descriptions and optional icons for each option.
              </p>
              <DescriptiveSelect
                value={descriptiveSelectValue}
                onChange={setDescriptiveSelectValue}
                placeholder="Choose a book type..."
                className="max-w-md"
                options={[
                  {
                    value: 'fiction',
                    label: 'Fiction',
                    description: 'Novels, short stories, and imaginative narratives',
                    icon: (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                      </svg>
                    ),
                  },
                  {
                    value: 'non_fiction',
                    label: 'Non-Fiction',
                    description: 'Educational, informational, and factual content',
                    icon: (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    ),
                  },
                  {
                    value: 'poetry',
                    label: 'Poetry',
                    description: 'Verses, poems, and lyrical expressions',
                    icon: (
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    ),
                  },
                ]}
              />
            </div>

            {/* AutoResizeTextarea */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">
                AutoResizeTextarea
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">
                Automatically grows with content. Try typing multiple lines.
              </p>
              <AutoResizeTextarea
                value={textareaValue}
                onChange={(e) => setTextareaValue(e.target.value)}
                placeholder="Type here... the textarea will grow automatically"
                minRows={2}
                maxRows={8}
              />
            </div>
          </div>
        </section>

        {/* MarkdownEditor Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            MarkdownEditor
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            A rich text editor with markdown support, toolbar for formatting, code blocks with
            syntax highlighting, math equations, and image upload.
          </p>
          <div className="space-y-4">
            <MarkdownEditor
              value={markdownValue}
              onChange={setMarkdownValue}
              placeholder="Write your content here..."
              label="Content"
              minHeight="200px"
            />
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Raw markdown output:</p>
              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                {markdownValue}
              </pre>
            </div>
          </div>
        </section>

        {/* Spinner Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Spinner
          </h2>
          <div className="flex items-center gap-8">
            <div className="text-center">
              <Spinner size="sm" />
              <p className="mt-2 text-xs text-gray-500">Small</p>
            </div>
            <div className="text-center">
              <Spinner size="md" />
              <p className="mt-2 text-xs text-gray-500">Medium</p>
            </div>
            <div className="text-center">
              <Spinner size="lg" />
              <p className="mt-2 text-xs text-gray-500">Large</p>
            </div>
          </div>
        </section>

        {/* Card Section */}
        <section>
          <SectionHeader description="A versatile container component with border and optional shadow.">
            Card
          </SectionHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <p className="text-gray-700 dark:text-gray-300">Default card with medium padding</p>
              </Card>
              <Card variant="elevated">
                <p className="text-gray-700 dark:text-gray-300">Elevated card with shadow</p>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card padding="sm">
                <p className="text-gray-700 dark:text-gray-300 text-sm">Small padding</p>
              </Card>
              <Card padding="md">
                <p className="text-gray-700 dark:text-gray-300 text-sm">Medium padding</p>
              </Card>
              <Card padding="lg">
                <p className="text-gray-700 dark:text-gray-300 text-sm">Large padding</p>
              </Card>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;Card variant=&quot;default|elevated&quot; padding=&quot;sm|md|lg&quot;&gt;...&lt;/Card&gt;
              </code>
            </p>
          </div>
        </section>

        {/* StatCard Section */}
        <section>
          <SectionHeader description="Display statistics in a card format with customizable colors.">
            StatCard
          </SectionHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard value="1,234" label="Total Items" />
              <StatCard value="42" label="Errors" color="red" />
              <StatCard value="89%" label="Success Rate" color="green" />
              <StatCard value="$99.50" label="Revenue" color="amber" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard value="5.2K" label="Input Tokens" color="blue" />
              <StatCard value="3.1K" label="Output Tokens" color="purple" />
              <StatCard value="150ms" label="Avg Duration" color="gray" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;StatCard value=&quot;...&quot; label=&quot;...&quot; color=&quot;default|red|blue|green|purple|amber|gray&quot; /&gt;
              </code>
            </p>
          </div>
        </section>

        {/* FormLabel Section */}
        <section>
          <SectionHeader description="Consistent label styling for form fields.">
            FormLabel
          </SectionHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <FormLabel>Regular Label</FormLabel>
                <Input placeholder="Example input" />
              </div>
              <div>
                <FormLabel required>Required Field</FormLabel>
                <Input placeholder="This field is required" />
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;FormLabel htmlFor=&quot;...&quot; required&gt;Label Text&lt;/FormLabel&gt;
              </code>
            </p>
          </div>
        </section>

        {/* SectionHeader Section */}
        <section>
          <SectionHeader description="Section titles with bottom border, used throughout this page.">
            SectionHeader
          </SectionHeader>
          <div className="space-y-4">
            <Card>
              <SectionHeader>Default Section Header</SectionHeader>
              <p className="text-gray-600 dark:text-gray-400">Content below the header...</p>
            </Card>
            <Card>
              <SectionHeader description="This is an optional description that appears below the title.">
                With Description
              </SectionHeader>
              <p className="text-gray-600 dark:text-gray-400">Content below the header...</p>
            </Card>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;SectionHeader description=&quot;...&quot; as=&quot;h1|h2|h3&quot;&gt;Title&lt;/SectionHeader&gt;
              </code>
            </p>
          </div>
        </section>

        {/* DropdownMenu Section */}
        <section>
          <SectionHeader description="A positioned dropdown menu with keyboard support and click-outside handling.">
            DropdownMenu
          </SectionHeader>
          <div className="space-y-4">
            <div className="relative inline-block">
              <Button onClick={() => setShowDropdown(!showDropdown)}>
                Toggle Dropdown
              </Button>
              <DropdownMenu isOpen={showDropdown} onClose={() => setShowDropdown(false)}>
                <DropdownMenuItem onClick={() => setShowDropdown(false)}>Edit</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDropdown(false)}>Duplicate</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowDropdown(false)}>Archive</DropdownMenuItem>
                <DropdownMenuItem variant="danger" onClick={() => setShowDropdown(false)}>
                  Delete
                </DropdownMenuItem>
              </DropdownMenu>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Usage:{' '}
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                &lt;DropdownMenu isOpen=&#123;bool&#125; onClose=&#123;fn&#125; position=&quot;left|right&quot;&gt;
                  &lt;DropdownMenuItem&gt;...&lt;/DropdownMenuItem&gt;
                &lt;/DropdownMenu&gt;
              </code>
            </p>
          </div>
        </section>

        {/* Modal Section */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Modal
          </h2>
          <div>
            <Button onClick={() => setShowModal(true)}>Open Modal</Button>
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Example Modal">
              <div className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  This is an example modal dialog. It can contain any content and automatically
                  handles keyboard events (Escape to close) and click outside to dismiss.
                </p>
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={() => setShowModal(false)}>Confirm</Button>
                </div>
              </div>
            </Modal>
          </div>
        </section>

        {/* Component List Reference */}
        <section>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Available Components
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            All components exported from{' '}
            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">components/common</code>:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {[
              'AiGenerateDropdownButton',
              'AiEnhancedInput',
              'AiEnhancedTextarea',
              'AutoResizeTextarea',
              'Button',
              'Card',
              'DropdownMenu',
              'FormLabel',
              'SaveButton',
              'SectionHeader',
              'StatCard',
              'DescriptiveSelect',
              'Input',
              'MarkdownEditor',
              'Modal',
              'Select',
              'Spinner',
              'TimedProgress',
              'TitleSuggestionPicker',
            ].map((component) => (
              <div
                key={component}
                className="px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm font-mono text-gray-700 dark:text-gray-300"
              >
                {component}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
